import * as React from 'react';

import { useOverlay, useOverlayPosition } from '@react-aria/overlays';
import { cn } from '@rematter/ui-web/lib';
import keyBy from 'lodash/keyBy';

// We need to create a simple useFieldInput that matches our needs

import { useDebouncedValue } from '~/shared/hooks/useDebounce';

interface DeductionOption {
  id: string;
  label: string;
  disabled?: boolean;
}

interface LineItemOption {
  id: string;
  label: string;
  disabled?: boolean;
}

interface DeductionFieldProps {
  className?: string;
  selectedDeductionTypeId?: string;
  deductionTypeOptions: DeductionOption[];
  onChangeDeductionType?: (id: string) => void;
  selectedLineItemId?: string;
  lineItemOptions: LineItemOption[];
  onChangeLineItem?: (id: string) => void;
  hideSecondInput?: boolean;
  disabled?: boolean;
}

type FilterableOption = {
  id: string;
  label: string;
  disabled?: boolean;
};

type FilteredOption = {
  option: FilterableOption;
  index: number | undefined;
  show: boolean;
};

// Simple useFieldInput for our specific case
const useFieldInput = <O extends FilterableOption = FilterableOption>({
  selectedId,
  onChange,
  onConfirm,
  options: allOptions,
}: {
  options: O[];
  selectedId: string | undefined;
  onConfirm?: (opt: { shouldFocusOnNextInnerField: boolean }) => void;
  onChange?: (optionId: string) => void;
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [popoverIsOpen, setPopoverIsOpen] = React.useState(false);
  const [resultsLength, setResultsLength] = React.useState(0);
  const [popoverFocusIndex, setPopoverFocusIndex] = React.useState(0);
  const [isMouseSelectionEnabled, setIsMouseSelectionEnabled] =
    React.useState(true);

  const optionsById = React.useMemo(
    () => keyBy(allOptions, (it) => it.id),
    [allOptions],
  );

  const currentOption = React.useMemo<O | undefined>(
    () => (selectedId ? optionsById?.[selectedId] : undefined),
    [optionsById, selectedId],
  );

  const selectedLabel = React.useMemo(
    () => currentOption?.label.trim() || '',
    [currentOption?.label],
  );
  const [query, setQuery] = React.useState(selectedLabel);
  const [popoverFilteredOptions, setPopoverFilteredOptions] = React.useState<
    FilteredOption[]
  >([]);

  const queryRef = React.useRef(query);
  queryRef.current = query;

  // Update query when selectedLabel changes and query is not in sync
  React.useEffect(() => {
    if (selectedLabel !== queryRef.current) {
      setQuery(selectedLabel);
    }
  }, [selectedLabel]);

  const debouncedQuery = useDebouncedValue(query, 200);

  const handleOptionClick = React.useCallback(
    (option: FilterableOption) => () => {
      if (option.disabled) {
        return;
      }

      inputRef.current?.blur();
      onChange?.(option.id);
      setQuery(option.label);
    },
    [onChange],
  );

  // Update popoverFilteredOptions when debouncedQuery changes
  React.useEffect(() => {
    requestAnimationFrame(() => {
      const filteredOptions =
        !debouncedQuery || debouncedQuery === selectedLabel
          ? allOptions
          : allOptions.filter((option) =>
              option.label
                ?.toLowerCase()
                .includes(debouncedQuery?.toLowerCase() ?? ''),
            );

      const filteredOptionIndexById = Object.fromEntries(
        filteredOptions.map((it, index) => [it.id, index]),
      );

      setPopoverFilteredOptions(
        allOptions.map((option) => ({
          option,
          index: filteredOptionIndexById[option.id],
          show: option.id in filteredOptionIndexById,
        })),
      );

      setResultsLength(filteredOptions.length);
      setPopoverFocusIndex(0);
      setPopoverIsOpen(
        inputRef.current === document.activeElement &&
          filteredOptions.length > 0,
      );
    });
  }, [debouncedQuery, allOptions, selectedLabel]);

  const handleEnter = React.useCallback(
    (e: React.KeyboardEvent) => {
      const newOptionSelection = popoverIsOpen
        ? popoverFilteredOptions.find((it) => it.index === popoverFocusIndex)
        : undefined;

      if (
        newOptionSelection?.option.disabled ||
        (!selectedId && !newOptionSelection)
      ) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (newOptionSelection) {
        // Use the handleOptionClick function to ensure consistent behavior
        handleOptionClick(newOptionSelection.option)();
      } else if (selectedId) {
        setQuery(optionsById[selectedId]?.label || '');
        inputRef.current?.blur();
      }

      // For first line, always try to focus next field
      const willFocusOnNextInnerField = !!onConfirm && newOptionSelection;
      const skipNextInnerField =
        popoverIsOpen && selectedId !== newOptionSelection?.option.id;

      if (!skipNextInnerField && willFocusOnNextInnerField) {
        onConfirm?.({
          shouldFocusOnNextInnerField: true,
        });
      }
    },
    [
      popoverIsOpen,
      popoverFilteredOptions,
      selectedId,
      popoverFocusIndex,
      handleOptionClick,
      optionsById,
      onConfirm,
    ],
  );

  const handleEscape = React.useCallback(() => {
    inputRef.current?.blur();
    if (selectedId) setQuery(optionsById[selectedId]?.label);
  }, [selectedId, optionsById]);

  const handleArrowDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      e.preventDefault();

      if (popoverIsOpen) {
        e.stopPropagation();
        setPopoverFocusIndex((i) => Math.min(resultsLength - 1, i + 1));
      } else if (selectedId && query && onConfirm) {
        e.stopPropagation();
        onConfirm({ shouldFocusOnNextInnerField: true });
      }
    },
    [popoverIsOpen, selectedId, query, resultsLength, onConfirm],
  );

  const handleArrowUp = React.useCallback(
    (e: React.KeyboardEvent) => {
      e.preventDefault();
      if (popoverIsOpen) {
        e.stopPropagation();
        setPopoverFocusIndex((i) => Math.max(0, i - 1));
      }
    },
    [popoverIsOpen],
  );

  const mouseMoveHandler = React.useCallback(() => {
    setIsMouseSelectionEnabled(true);
    window.removeEventListener('mousemove', mouseMoveHandler);
  }, []);

  const handleLetterOrNumbers = React.useCallback<React.KeyboardEventHandler>(
    (e) => {
      e.stopPropagation();
      setIsMouseSelectionEnabled(false);
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.addEventListener('mousemove', mouseMoveHandler);
    },
    [mouseMoveHandler],
  );

  const handleQueryKeyDown = React.useCallback<React.KeyboardEventHandler>(
    (e) => {
      if (e.key === 'ArrowUp') {
        handleArrowUp(e);
      } else if (e.key === 'ArrowDown') {
        handleArrowDown(e);
      } else if (e.key === 'Enter') {
        handleEnter(e);
      } else if (e.key === 'Escape') {
        handleEscape();
      } else if (/^[A-Z0-9.-_#\s]$/i.test(e.key)) {
        handleLetterOrNumbers(e);
      }
    },
    [
      handleArrowUp,
      handleArrowDown,
      handleEnter,
      handleEscape,
      handleLetterOrNumbers,
    ],
  );

  const closePopover = React.useCallback(() => setPopoverIsOpen(false), []);

  return {
    inputRef,
    hasEmptyResults: resultsLength === 0,
    currentOption,
    isMouseSelectionEnabled,
    popoverFilteredOptions,
    popoverFocusIndex,
    popoverIsOpen,
    setPopoverIsOpen,
    closePopover,
    selectedLabel,
    setPopoverFocusIndex,
    query,
    setQuery,
    handleOptionClick,
    handleQueryKeyDown,
  };
};

const firstLineStyles = cn('px-3 text-sm leading-[1.2]');
const secondLineStyles = cn(
  'px-3 text-[11px] leading-[1.2] font-normal !text-neutral/90',
);
const seamlessFieldStyles = cn(
  'border-none bg-transparent p-0 outline-none placeholder:text-neutral/60',
);

const defer = setTimeout;

// TODO: Fix this, and make it generic so that we don't have multiple components using a popover
export const DeductionField = ({
  className,
  selectedDeductionTypeId,
  deductionTypeOptions,
  onChangeDeductionType,
  selectedLineItemId,
  lineItemOptions,
  onChangeLineItem,
  hideSecondInput,
  disabled,
}: DeductionFieldProps) => {
  const [innerFocus, setInnerFocus] = React.useState<
    'firstLine' | 'secondLine' | undefined
  >();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const firstLineOverlayRef = React.useRef<HTMLDivElement>(null);
  const secondLineOverlayRef = React.useRef<HTMLDivElement>(null);

  const secondLineState = useFieldInput({
    options: lineItemOptions ?? [],
    selectedId: selectedLineItemId,
    onChange: onChangeLineItem,
  });

  const handleFirstLineConfirmation = React.useCallback(
    ({
      shouldFocusOnNextInnerField,
    }: {
      shouldFocusOnNextInnerField: boolean;
    }) => {
      const newInnerFocus =
        shouldFocusOnNextInnerField && !hideSecondInput
          ? 'secondLine'
          : undefined;

      setInnerFocus(newInnerFocus);

      if (newInnerFocus === 'secondLine') {
        if (secondLineState.inputRef.current) {
          secondLineState.inputRef.current.focus();
          secondLineState.inputRef.current.select();
        } else {
          setTimeout(() => {
            secondLineState.inputRef.current?.focus();
            secondLineState.inputRef.current?.select();
          });
        }
      }
    },
    [secondLineState, hideSecondInput],
  );

  const firstLineState = useFieldInput({
    options: deductionTypeOptions ?? [],
    selectedId: selectedDeductionTypeId,
    onChange: onChangeDeductionType,
    onConfirm: handleFirstLineConfirmation,
  });

  const handleSecondLineKeyDown = React.useCallback<React.KeyboardEventHandler>(
    (e) => {
      switch (e.key) {
        case 'ArrowUp':
          firstLineState.inputRef.current?.focus();
          e.preventDefault();
          e.stopPropagation();
          break;

        case 'ArrowDown':
        case 'Enter':
        case 'Escape':
        case 'Tab':
          // Do nothing: let the event propagate
          break;

        default:
          e.stopPropagation();
          break;
      }
    },
    [firstLineState.inputRef],
  );

  const handleFirstLineFocus = React.useCallback<
    React.FocusEventHandler<HTMLInputElement>
  >(
    (e) => {
      setInnerFocus('firstLine');
      e.target.select();
      // Reset focused item
      firstLineState.setPopoverFocusIndex(-1);
    },

    [firstLineState.setPopoverFocusIndex],
  );

  const handleSecondLineFocus = React.useCallback<
    React.FocusEventHandler<HTMLInputElement>
  >(
    (e) => {
      setInnerFocus('secondLine');
      e.target.select();
      secondLineState.setPopoverFocusIndex(-1);
    },

    [secondLineState.setPopoverFocusIndex],
  );

  const handleFirstLineChange = React.useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (e) => {
      firstLineState.setQuery(e.target.value);
    },
    [firstLineState],
  );

  const handleSecondLineInputChange = React.useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (e) => {
      secondLineState.setQuery(e.target.value);
    },
    [secondLineState],
  );

  const handleFirstLineBlur = React.useCallback(() => {
    // Delay closing to allow clicks on options
    setTimeout(() => {
      setInnerFocus(undefined);
      firstLineState.closePopover();
    }, 200);
  }, [setInnerFocus, firstLineState]);

  const handleSecondLineBlur = React.useCallback(() => {
    // Delay closing to allow clicks on options
    setTimeout(() => {
      setInnerFocus(undefined);
      secondLineState.closePopover();
    }, 200);
  }, [setInnerFocus, secondLineState]);

  // First line overlay hooks
  const { overlayProps: firstLineOverlayProps } = useOverlay(
    {
      isOpen: firstLineState.popoverIsOpen,
      onClose: () => firstLineState.closePopover(),
      isDismissable: true,
      shouldCloseOnInteractOutside: (element) =>
        !containerRef.current?.contains(element),
    },
    firstLineOverlayRef,
  );

  const { overlayProps: firstLinePositionProps } = useOverlayPosition({
    targetRef: containerRef,
    overlayRef: firstLineOverlayRef,
    placement: 'bottom start',
    offset: 8,
    isOpen: firstLineState.popoverIsOpen,
    shouldFlip: true,
    containerPadding: 12,
  });

  // Second line overlay hooks
  const { overlayProps: secondLineOverlayProps } = useOverlay(
    {
      isOpen: secondLineState.popoverIsOpen,
      onClose: () => secondLineState.closePopover(),
      isDismissable: true,
      shouldCloseOnInteractOutside: (element) =>
        !containerRef.current?.contains(element),
    },
    secondLineOverlayRef,
  );

  const { overlayProps: secondLinePositionProps } = useOverlayPosition({
    targetRef: containerRef,
    overlayRef: secondLineOverlayRef,
    placement: 'bottom start',
    offset: 8,
    isOpen: secondLineState.popoverIsOpen,
    shouldFlip: true,
    containerPadding: 12,
  });

  return (
    <div className="@DeductionField relative">
      <div
        ref={containerRef}
        className={cn(
          'relative flex h-[40px] flex-row items-center rounded-md border-[1px] border-neutral/20 bg-white px-3 focus-within:border-primary',
          className,
          disabled && 'cursor-not-allowed !bg-neutral/5 *:!cursor-not-allowed',
          disabled && seamlessFieldStyles,
        )}
      >
        <div className={'flex w-full flex-col justify-center'}>
          {/* --------------- FIRST LINE --------------- */}
          <input
            ref={firstLineState.inputRef}
            value={firstLineState.query}
            className={cn(
              seamlessFieldStyles,
              firstLineStyles,
              innerFocus === 'firstLine' ? 'w-full' : 'size-0',
              firstLineState.hasEmptyResults ? 'text-failure' : '',
            )}
            onBlur={handleFirstLineBlur}
            onFocus={handleFirstLineFocus}
            onKeyDown={firstLineState.handleQueryKeyDown}
            onChange={handleFirstLineChange}
            disabled={disabled}
          />
          {innerFocus !== 'firstLine' &&
            (firstLineState.selectedLabel ? (
              <div
                className={cn(
                  firstLineStyles,
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                )}
                onClick={() => {
                  if (!disabled) {
                    firstLineState.inputRef.current?.focus();
                  }
                }}
              >
                {firstLineState.selectedLabel}
              </div>
            ) : (
              <div
                className={cn(firstLineStyles, 'cursor-pointer opacity-30')}
                onClick={() => {
                  if (!disabled) {
                    firstLineState.inputRef.current?.focus();
                  }
                }}
              >
                Select deduction type...
              </div>
            ))}
          {/* --------------- SECOND LINE --------------- */}
          {!hideSecondInput && (
            <div
              className={'flex w-full'}
              onMouseDown={(e) => {
                setTimeout(() => secondLineState.inputRef.current?.focus());
                e.stopPropagation();
              }}
            >
              {selectedLineItemId && !innerFocus && (
                <div
                  className={cn(
                    secondLineStyles,
                    'w-1 flex-grow overflow-clip text-ellipsis whitespace-nowrap opacity-60',
                  )}
                >
                  {secondLineState.selectedLabel}
                </div>
              )}
              <input
                ref={secondLineState.inputRef}
                className={cn(
                  secondLineStyles,
                  seamlessFieldStyles,
                  'opacity-60',
                  innerFocus === 'secondLine' ? 'w-full' : 'size-0',
                  secondLineState.hasEmptyResults ? 'text-failure' : '',
                )}
                value={secondLineState.query}
                onFocus={handleSecondLineFocus}
                placeholder="Select line item..."
                onChange={handleSecondLineInputChange}
                onBlur={handleSecondLineBlur}
                onKeyDown={(e) =>
                  secondLineState.popoverIsOpen
                    ? secondLineState.handleQueryKeyDown(e)
                    : handleSecondLineKeyDown(e)
                }
                disabled={disabled}
              />
            </div>
          )}
        </div>
      </div>

      {/* First line popover */}
      {firstLineState.popoverIsOpen && (
        <div
          ref={firstLineOverlayRef}
          {...firstLineOverlayProps}
          {...firstLinePositionProps}
          className="z-[100] overflow-hidden rounded-md border-[1px] border-neutral bg-neutral/80 shadow-lg"
          style={{
            ...firstLinePositionProps.style,
            position: 'absolute',
            minWidth: containerRef.current?.offsetWidth ?? 350,
            maxWidth: Math.max(containerRef.current?.offsetWidth ?? 350, 350),
          }}
        >
          <div className="no-scrollbar flex max-h-[30vh] w-full flex-col items-center gap-[1px] overflow-auto bg-neutral/80 pr-1">
            {firstLineState.popoverFilteredOptions.map(
              ({ option, index, show }) => (
                <div
                  key={option.id}
                  className={cn(
                    'group flex w-full cursor-pointer items-center rounded py-2 pr-6 pl-3 text-sm outline outline-0 select-none',
                    !show && 'hidden',
                    option.disabled &&
                      'cursor-not-allowed !bg-neutral/5 *:cursor-not-allowed',
                    disabled && 'cursor-not-allowed opacity-50',
                    firstLineState.popoverFocusIndex === index
                      ? 'bg-primary text-abs-white'
                      : 'text-abs-white hover:bg-primary-darker',
                  )}
                  onMouseDown={
                    option.disabled
                      ? undefined
                      : (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          firstLineState.handleOptionClick(option)();
                        }
                  }
                  onMouseEnter={() => {
                    firstLineState.isMouseSelectionEnabled &&
                      defer(
                        () =>
                          index !== undefined &&
                          firstLineState.setPopoverFocusIndex(index),
                      );
                  }}
                  onMouseLeave={() =>
                    firstLineState.isMouseSelectionEnabled &&
                    firstLineState.setPopoverFocusIndex(-1)
                  }
                >
                  {option.label}
                </div>
              ),
            )}
          </div>
        </div>
      )}

      {/* Second line popover */}
      {secondLineState.popoverIsOpen && (
        <div
          ref={secondLineOverlayRef}
          {...secondLineOverlayProps}
          {...secondLinePositionProps}
          className="z-[100] overflow-hidden rounded-md border-[1px] border-neutral bg-neutral/80 shadow-lg"
          style={{
            ...secondLinePositionProps.style,
            position: 'absolute',
            minWidth: containerRef.current?.offsetWidth ?? 350,
            maxWidth: Math.max(containerRef.current?.offsetWidth ?? 350, 350),
          }}
        >
          <div className="no-scrollbar flex max-h-[30vh] w-full flex-col gap-[1px] overflow-auto bg-neutral/80 pr-0">
            {secondLineState.popoverFilteredOptions.map(
              ({ option, index, show }) => (
                <div
                  key={option.id}
                  className={cn(
                    'group flex w-full cursor-pointer items-center rounded py-2 pr-6 pl-3 text-sm outline outline-0 select-none',
                    !show && 'hidden',
                    option.disabled && 'cursor-not-allowed opacity-50',
                    secondLineState.popoverFocusIndex === index
                      ? 'bg-primary text-abs-white'
                      : 'text-abs-white hover:bg-primary-darker',
                  )}
                  onMouseDown={
                    option.disabled
                      ? undefined
                      : (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          secondLineState.handleOptionClick(option)();
                        }
                  }
                  onMouseEnter={() =>
                    secondLineState.isMouseSelectionEnabled &&
                    defer(
                      () =>
                        index !== undefined &&
                        secondLineState.setPopoverFocusIndex(index),
                    )
                  }
                  onMouseLeave={() =>
                    secondLineState.isMouseSelectionEnabled &&
                    secondLineState.setPopoverFocusIndex(-1)
                  }
                >
                  {option.label}
                </div>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
};
